import { useCallback, useEffect, useMemo, useState } from "react";
import { getManagerEvents, importManagerEvents } from "@/api/manager";

export type ManagerEvent = {
  id: string;
  name?: string | null;
  event_date?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  venue?: string | null;
  source?: string | null;
  source_urls?: string[] | null;
  supporting_acts?: string[] | null;
  attendance?: number | null;
  ticket_gross?: number | null;
  merch_revenue?: number | null;
  revenue?: number | null;
  guarantee_amount?: number | null;
  split_terms?: string | null;
  artist_percentage?: number | null;
  sound_expenses?: number | null;
  house_expenses?: number | null;
  notes?: string | null;
  accounting_entry_id?: string | null;
  is_manual?: boolean | null;
};

type BandsintownMetadata = {
  last_sync_at?: string | null;
};

export function useManagerEvents(limit = 100) {
  const [events, setEvents] = useState<ManagerEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await getManagerEvents(limit);
      const list = Array.isArray(payload?.events)
        ? (payload.events as ManagerEvent[])
        : Array.isArray(payload)
          ? (payload as ManagerEvent[])
          : [];
      setEvents(list);
      const bandsintownMeta = (payload?.bandsintown ?? {}) as BandsintownMetadata;
      setLastSyncAt(bandsintownMeta?.last_sync_at ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load events");
    } finally {
      setLoading(false);
    }
  }, [limit]);

  const sync = useCallback(
    async (dateFilter: "upcoming" | "past" | "all" = "all") => {
      setSyncing(true);
      setError(null);
      try {
        const payload = await importManagerEvents({ date_filter: dateFilter });
        if (payload?.last_sync_at) {
          setLastSyncAt(payload.last_sync_at);
        }
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to sync Bandsintown events");
      } finally {
        setSyncing(false);
      }
    },
    [load],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const { upcomingEvents, pastEvents } = useMemo(() => {
    const now = Date.now();
    const bucketed = events.reduce<{
      upcoming: ManagerEvent[];
      past: ManagerEvent[];
    }>(
      (acc, event) => {
        const timestamp = event.event_date ? new Date(event.event_date).getTime() : null;
        if (timestamp && timestamp >= now) {
          acc.upcoming.push(event);
        } else {
          acc.past.push(event);
        }
        return acc;
      },
      { upcoming: [], past: [] },
    );
    bucketed.upcoming.sort((a, b) => {
      const aTime = a.event_date ? new Date(a.event_date).getTime() : 0;
      const bTime = b.event_date ? new Date(b.event_date).getTime() : 0;
      return aTime - bTime;
    });
    bucketed.past.sort((a, b) => {
      const aTime = a.event_date ? new Date(a.event_date).getTime() : 0;
      const bTime = b.event_date ? new Date(b.event_date).getTime() : 0;
      return bTime - aTime;
    });
    return { upcomingEvents: bucketed.upcoming, pastEvents: bucketed.past };
  }, [events]);

  return {
    events,
    upcomingEvents,
    pastEvents,
    loading,
    syncing,
    error,
    refresh: load,
    sync,
    lastSyncAt,
  };
}
